import { useMemo } from "react";
import { useQuery } from "@connectrpc/connect-query";
import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { nepalToday } from "@yatayat/core/date";
import { ConsoleService } from "@yatayat/gen/yatayat/console/v1/console_pb";

import { resolveRange } from "~/components/charts/range";
import { RangePicker } from "~/components/charts/RangePicker";
import { zeroFillDaily } from "~/components/charts/series";
import { DemandFestivals } from "~/components/console/DemandFestivals";
import { DemandOdMatrix } from "~/components/console/DemandOdMatrix";
import { DemandSales } from "~/components/console/DemandSales";
import { DemandSummary } from "~/components/console/DemandSummary";
import { DemandTrend } from "~/components/console/DemandTrend";
import { DemandWeekdays } from "~/components/console/DemandWeekdays";
import { useStaffSessionExpiry } from "~/components/OperatorTrip";
import { Loading, QueryError } from "~/components/QueryState";
import { useI18n } from "~/lib/i18n";
import { formatIsoDate } from "~/lib/labels";
import { retryTransient } from "~/lib/rpc/errors";
import { searchString } from "~/lib/search";
import { ui } from "~/lib/ui";

type DemandSearch = { corridor?: string; from?: string; to?: string };

// The filters live in the URL so the overview's corridor panel can link straight here.
export const Route = createFileRoute("/console/demand")({
  validateSearch: (search: Record<string, unknown>): DemandSearch => ({
    corridor: searchString(search.corridor),
    from: searchString(search.from),
    to: searchString(search.to),
  }),
  component: ConsoleDemand,
});

const corridorPattern = /^[A-Z0-9-]{3,32}$/;
const fieldClass = "rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900";

function ConsoleDemand() {
  const { t, lang } = useI18n();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const today = nepalToday();
  const range = resolveRange(search, today);
  const corridor = search.corridor && corridorPattern.test(search.corridor) ? search.corridor : "";
  const update = (patch: DemandSearch) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true, resetScroll: false });

  const query = { retry: retryTransient, placeholderData: keepPreviousData };
  const corridors = useQuery(ConsoleService.method.listCorridors, { range }, query);
  const demand = useQuery(ConsoleService.method.getDemand, { range, corridorCode: corridor }, query);
  const unauthenticated = useStaffSessionExpiry(demand.error);

  const data = demand.data;
  const busy = demand.isPlaceholderData;
  const { from, to } = range;
  const days = useMemo(() => {
    if (!data) return [];
    const rows = data.daily.map((d) => ({ date: d.date, tickets: d.tickets, seatsOffered: d.seatsOffered }));
    // While an older answer stands in for a new range, draw it over its own dates.
    const dates = rows.map((r) => r.date).sort();
    return busy && dates.length > 0 ? zeroFillDaily(rows, dates[0], dates[dates.length - 1]) : zeroFillDaily(rows, from, to);
  }, [data, busy, from, to]);

  const corridorList = corridors.data?.corridors ?? [];
  const corridorName = corridor ? corridorList.find((c) => c.code === corridor)?.name?.[lang] || corridor : t("consoleAllCorridors");

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="min-w-0">
          <h1 className={ui.h1}>{t("consoleDemandTitle")}</h1>
          <p className="text-slate-600 dark:text-slate-400">
            {corridorName} · {t("consoleRangeLabel", { from: formatIsoDate(range.from, lang), to: formatIsoDate(range.to, lang) })}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-4 rounded-xl bg-slate-50 p-3 dark:bg-slate-900">
          <RangePicker value={range} today={today} onChange={(r) => update({ from: r.from, to: r.to })} />
          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t("consoleCorridor")}</span>
            <select value={corridor} onChange={(e) => update({ corridor: e.target.value || undefined })} className={fieldClass}>
              <option value="">{t("consoleAllCorridors")}</option>
              {corridorList.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name?.[lang] || c.code}
                </option>
              ))}
              {corridor !== "" && !corridorList.some((c) => c.code === corridor) && <option value={corridor}>{corridor}</option>}
            </select>
          </label>
        </div>
      </div>

      {demand.isPending ? (
        <Loading />
      ) : !data ? (
        !unauthenticated && <QueryError onRetry={() => void demand.refetch()} busy={demand.isFetching} />
      ) : (
        <>
          <div className={`transition-opacity ${busy ? "opacity-60" : ""}`}>
            <DemandSummary days={days} />
          </div>
          <DemandTrend days={days} festivals={data.festivals} busy={busy} />
          <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <DemandWeekdays weekdays={data.weekdays} busy={busy} />
            <DemandFestivals festivals={data.festivals} busy={busy} />
          </div>
          <DemandOdMatrix places={data.places} od={data.od} busy={busy} />
        </>
      )}

      <DemandSales range={range} />
    </div>
  );
}
